/**
 * UserSearchInput Component
 *
 * A typeahead input that searches Microsoft Entra ID users
 * and allows selection to populate form fields.
 */

import { Spinner } from '@medsuite/ui-components';
import React, { useState, useRef, useEffect, useCallback } from 'react';

import { useGraphUserSearch, GraphUser } from '../../hooks/useGraphUserSearch';

/**
 * Props for UserSearchInput
 */
interface UserSearchInputProps {
  /**
   * Label for the input field
   */
  label?: string;

  /**
   * Current value displayed in the input
   */
  value: string;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Whether the field is required
   */
  required?: boolean;

  /**
   * Whether the input is disabled
   */
  disabled?: boolean;

  /**
   * Callback when a user is selected from the dropdown
   */
  onUserSelect: (user: GraphUser) => void;

  /**
   * Callback when the input value changes manually (not from selection)
   */
  onInputChange?: (value: string) => void;
}

/**
 * Debounce delay for search queries (ms)
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * UserSearchInput Component
 */
export function UserSearchInput({
  label,
  value,
  placeholder = 'Search by name or email...',
  required = false,
  disabled = false,
  onUserSelect,
  onInputChange,
}: UserSearchInputProps) {
  const [inputValue, setInputValue] = useState(value);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const { users, isSearching, error, searchUsers, clearResults } = useGraphUserSearch();

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync input value with prop
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setHighlightedIndex(-1);
      onInputChange?.(newValue);

      // Clear previous debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (newValue.length >= 2) {
        setShowDropdown(true);
        debounceRef.current = setTimeout(() => {
          searchUsers(newValue);
        }, SEARCH_DEBOUNCE_MS);
      } else {
        clearResults();
        setShowDropdown(false);
      }
    },
    [searchUsers, clearResults, onInputChange]
  );

  // Handle user selection
  const handleSelectUser = useCallback(
    (user: GraphUser) => {
      setInputValue(user.displayName);
      setShowDropdown(false);
      clearResults();
      onUserSelect(user);
    },
    [onUserSelect, clearResults]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showDropdown || users.length === 0) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < users.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < users.length) {
            handleSelectUser(users[highlightedIndex]);
          }
          break;
        case 'Escape':
          setShowDropdown(false);
          break;
      }
    },
    [showDropdown, users, highlightedIndex, handleSelectUser]
  );

  // Handle focus
  const handleFocus = useCallback(() => {
    if (users.length > 0) {
      setShowDropdown(true);
    }
  }, [users.length]);

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
          autoComplete="off"
        />

        {isSearching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <Spinner size="sm" />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (users.length > 0 || isSearching || error) && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg max-h-60 overflow-auto"
        >
          {error && <div className="px-4 py-3 text-sm text-red-600 dark:text-red-400">{error}</div>}

          {isSearching && users.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Spinner size="sm" />
              Searching...
            </div>
          )}

          {!isSearching && users.length === 0 && !error && inputValue.length >= 2 && (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No users found</div>
          )}

          {users.map((user, index) => (
            <button
              key={user.id}
              type="button"
              onClick={() => handleSelectUser(user)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none ${
                index === highlightedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
              }`}
            >
              <div className="font-medium text-gray-900 dark:text-gray-100">{user.displayName}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {user.mail || user.userPrincipalName}
                {user.jobTitle && ` - ${user.jobTitle}`}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserSearchInput;
